import { useState, useRef, useEffect, useCallback } from 'react';

import EditIcon from '@lib/core/src/assets/buttons-icons/edit.svg?react';
import ThreeDotsIcon from '@lib/core/src/assets/buttons-icons/three-dots-black.svg?react';
import DeleteIcon from '@lib/core/src/assets/buttons-icons/trash.svg?react';
import Popover from '@lib/core/src/components/Popover';
import TextInput from '@lib/core/src/components/TextInput';
import palette from '@lib/core/src/theme/palette';
import { Divider } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';

import { ThreadResponse, ThreadDto, getListAgentThreadsQueryKey } from '@/api/generated';
import { ThreadRowRedDot } from '@/components/AskMeAnything/ChatStyle';
import {
  ThreadName,
  EditBoxText,
  EditBoxItem,
  ThreeDotsIconButton
} from '@/components/AskMeAnything/components/threads/ThreadsStyle';
import { findThreadToSetActive } from '@/components/AskMeAnything/utils/chatUtil';
import useThreads from '@/hooks/askMeAnything/useThreads';
import { useActiveThreadId } from '@/store/chat/chatStore';
import { launchErrorToast } from '@/utils/toast';

const popoverSx = {
  backgroundColor: palette.white,
  width: '165px',
  height: '64px',
  borderRadius: '8px',
  padding: 0,
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  border: `1px solid ${palette.grey[300]}`,
  boxShadow: '0px 6px 12px -6px rgba(24, 39, 75, 0.12), 0px 8px 24px -4px rgba(24, 39, 75, 0.08)'
};

interface ThreadRowProps {
  thread: ThreadDto;
  originalName: string;
  threadId: string;
  onClick: () => void;
  setChatTitle: (title: string) => void;
  handleThreadChange: (thread: ThreadDto, willCloseThreads?: boolean) => void;
}

const ThreadRow = ({ thread, onClick, setChatTitle, handleThreadChange }: ThreadRowProps) => {
  const { patchThread, deleteThread, threads } = useThreads();
  const threadsQueryKey = getListAgentThreadsQueryKey();
  const queryClient = useQueryClient();
  const [name, setName] = useState(thread.title || '');
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const activeThreadId = useActiveThreadId();
  const threadId = thread.id || '';

  useEffect(() => {
    if (isEditing) {
      inputRef.current?.focus();
    }
  }, [isEditing]);

  const handleRename = (value: string) => {
    setIsEditing(false);
    const previousData = queryClient.getQueryData<ThreadResponse>(threadsQueryKey);
    queryClient.setQueryData(threadsQueryKey, (oldData: ThreadResponse) => {
      if (!oldData) return oldData;
      return {
        ...oldData,
        items: oldData.items?.map((thread: ThreadDto) =>
          thread.id === threadId ? { ...thread, title: value } : thread
        )
      };
    });

    patchThread(
      { id: threadId, data: { title: value } },
      {
        onError: () => {
          launchErrorToast('Failed to rename thread');
          queryClient.setQueryData(threadsQueryKey, previousData);
          setName(thread.title || '');
        },
        onSuccess: () => {
          if (activeThreadId === threadId) {
            setChatTitle(value);
          }
        }
      }
    );
  };

  const handleDelete = useCallback(() => {
    if (!threadId || !threads.length) return;
    deleteThread(
      { threadId },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: threadsQueryKey });
          if (activeThreadId !== threadId) return;
          const newActiveThread = findThreadToSetActive(threads, activeThreadId);
          handleThreadChange(newActiveThread);
        },
        onError: () => {
          launchErrorToast('Failed to delete thread');
        }
      }
    );
  }, [
    deleteThread,
    queryClient,
    threadsQueryKey,
    threads,
    activeThreadId,
    handleThreadChange,
    threadId
  ]);

  const handleEdit = useCallback(() => {
    setIsEditing(true);
    setIsPopoverOpen(false);
  }, []);

  const isThreadUnseen = thread.isUserCreated === false && !thread.seenAt;

  return (
    <ThreadName
      unseen={isThreadUnseen}
      active={activeThreadId === threadId}
      ref={containerRef}
      onClick={e => {
        if (!isPopoverOpen && !buttonRef.current?.contains(e.target as Node)) {
          onClick();
        }
      }}
    >
      {isThreadUnseen && <ThreadRowRedDot />}
      {isEditing ? (
        <TextInput
          ref={inputRef}
          type="text"
          placeholder={name}
          value={name}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setName(e.target.value)}
          onClick={e => e.stopPropagation()}
          onKeyDown={e => {
            if (e.key === 'Enter') {
              handleRename(name);
            }
          }}
          onBlur={() => handleRename(name)}
          style={{
            color: palette.black
          }}
        />
      ) : (
        name
      )}
      <Popover
        trigger={
          <ThreeDotsIconButton
            ref={buttonRef}
            onClick={e => {
              e.stopPropagation();
              setIsPopoverOpen(!isPopoverOpen);
            }}
            size="small"
            className="three-dots-icon"
          >
            <ThreeDotsIcon />
          </ThreeDotsIconButton>
        }
        open={isPopoverOpen}
        onOpen={() => setIsPopoverOpen(true)}
        onClose={() => setIsPopoverOpen(false)}
        anchorEl={containerRef.current}
        container={document.body}
        sx={popoverSx}
      >
        <EditBoxItem
          onClick={e => {
            e.stopPropagation();
            handleEdit();
          }}
        >
          <EditIcon />
          <EditBoxText>Rename title</EditBoxText>
        </EditBoxItem>
        <Divider sx={{ borderColor: palette.grey[300] }} />
        <EditBoxItem
          onClick={e => {
            e.stopPropagation();
            handleDelete();
          }}
        >
          <DeleteIcon />
          <EditBoxText>Delete thread</EditBoxText>
        </EditBoxItem>
      </Popover>
    </ThreadName>
  );
};

export default ThreadRow;
